import { useState } from "react";
import {
  ChangePasswordFormType,
  LoginFormType,
  SignUpFormType,
  UpdateProfileFormType,
} from "../types/forms";

import { AxiosError } from "axios";
import useAxios from "../hooks/useAxios";
import axiosMain from "axios";
import { useNavigate } from "react-router-dom";
import { TokenHelper } from "../utils/tokensHelper";
import { useAuthStore } from "../stores/authStore";
import { failedToast, successToast } from "../utils/toasts";
import { ApiModules } from "@/types/enums/api-modules";
import { User } from "@/types/entities/user";
import {
  ApiErrorResponse,
  AuthTokenResponse,
  DetailResponse,
  MessageResponse,
  RefreshTokenResponse,
  RegisterResponse,
  SignInResponse,
  TokenVerifyResponse,
  TwoFactorChallengeResponse,
} from "../types/response";

export const useSignUp = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const signUp = async (userFormData: SignUpFormType) => {
    try {
      setLoading(true);
      const { data } = await axios.post<RegisterResponse>("/auth/register/", {
        ...userFormData,
      });

      if (data) {
        successToast(data.message);
        return { success: true };
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(error);
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false, error };
    } finally {
      setLoading(false);
    }
    return { success: false };
  };

  return { loading, signUp };
};

export const useSignin = () => {
  const [loading, setLoading] = useState(false);

  const { axios } = useAxios(ApiModules.AUTH);

  const signIn = async (userFormData: LoginFormType) => {
    setLoading(true);
    try {
      const { data } = await axios.post<SignInResponse>("/auth/token/", {
        ...userFormData,
      });

      // 2FA challenge branch — no tokens yet, caller must verify the code
      if ("two_factor_required" in data) {
        return {
          success: false as const,
          twoFactorRequired: true as const,
          verificationId: data.verification_id,
          method: data.method,
          detail: data.detail,
        };
      }

      // Normal token branch — TS now knows `data` is AuthTokenResponse here
      if (data.access && data.refresh) {
        const tokenHelper = new TokenHelper();
        tokenHelper.setTokens(data.access, data.refresh);
        return { success: true as const };
      } else {
        throw new Error("Missing tokens in the API response");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;

      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, signIn };
};

export const useGoogleAuth = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const signInWithGoogle = async (idToken: string) => {
    setLoading(true);
    try {
      // ⚠️ GUESSED ENDPOINT — confirm with backend. Assumed to mirror
      // /auth/token/'s response shape, including the 2FA challenge branch.
      const { data } = await axios.post<SignInResponse>("/auth/google/", {
        id_token: idToken,
      });

      if ("two_factor_required" in data) {
        return {
          success: false as const,
          twoFactorRequired: true as const,
          verificationId: data.verification_id,
          method: data.method,
          detail: data.detail,
        };
      }

      if (data.access && data.refresh) {
        const tokenHelper = new TokenHelper();
        tokenHelper.setTokens(data.access, data.refresh);
        return { success: true as const };
      } else {
        throw new Error("Missing tokens in the API response");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, signInWithGoogle };
};

export const useVerify2FA = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const verify2FA = async (verificationId: string, code: string) => {
    setLoading(true);
    try {
      const { data } = await axios.post<AuthTokenResponse>(
        "/auth/2fa/verify/",
        { verification_id: verificationId, code },
      );

      if (data.access && data.refresh) {
        const tokenHelper = new TokenHelper();
        tokenHelper.setTokens(data.access, data.refresh);
        return { success: true as const };
      } else {
        throw new Error("Missing tokens in the API response");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, verify2FA };
};

export const useResend2FA = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const resend2FA = async (verificationId: string) => {
    setLoading(true);
    try {
      const { data } = await axios.post<TwoFactorChallengeResponse>(
        "/auth/2fa/resend/",
        { verification_id: verificationId },
      );

      successToast(data.detail);
      return {
        success: true as const,
        verificationId: data.verification_id,
        method: data.method,
        detail: data.detail,
      };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      failedToast(
        (typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message &&
              typeof error.response.data.message === "string"
            ? error.response.data.message
            : error.message) as string,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, resend2FA };
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { unsetUser } = useAuthStore();

  const logOutUser = () => {
    const tokenHelper = new TokenHelper();
    tokenHelper.deleteTokens();
    unsetUser();
    navigate("/");
  };

  return { logOutUser };
};

export const useGetProfile = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);
  const { setUser } = useAuthStore();

  const getProfile = async (): Promise<User | null> => {
    setLoading(true);

    try {
      const { data } = await axios.get<User>("/auth/me/");

      if (data) {
        // Update Zustand store with user data (excluding tokens)
        setUser({ ...data });
        return data;
      } else {
        throw new Error("Login failed: Invalid response from server");
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      failedToast(
        (typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.message &&
              typeof error.response.data.message === "string"
            ? error.response.data.message
            : error.message) as string,
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { loading, getProfile };
};

export const useUpdateProfile = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);
  const { setUser } = useAuthStore();

  const updateProfile = async (
    formData: UpdateProfileFormType,
    profilePicture?: File | null,
  ) => {
    setLoading(true);
    try {
      const payload = new FormData();

      if (formData.first_name !== undefined)
        payload.append("first_name", formData.first_name);
      if (formData.last_name !== undefined)
        payload.append("last_name", formData.last_name);
      if (formData.phone_number !== undefined)
        payload.append("phone_number", formData.phone_number);
      if (formData.newsletter_subscription !== undefined)
        payload.append(
          "newsletter_subscription",
          String(formData.newsletter_subscription),
        );
      if (profilePicture) payload.append("profile_picture", profilePicture);

      const { data } = await axios.put<User>("/auth/me/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUser({ ...data });
      successToast("Profil mis à jour avec succès.");
      return { success: true as const, data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateProfile };
};

export const useChangePassword = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const changePassword = async (formData: ChangePasswordFormType) => {
    setLoading(true);
    try {
      const { data } = await axios.post<MessageResponse>(
        "/auth/change-password/",
        { ...formData },
      );
      successToast(data.message);
      return { success: true as const, message: data.message };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, changePassword };
};

export const useDeactivateAccount = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);
  const { unsetUser } = useAuthStore();
  const navigate = useNavigate();

  const deactivateAccount = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post<MessageResponse>(
        "/auth/user/deactivate/",
        {},
      );

      successToast(data.message);

      const tokenHelper = new TokenHelper();
      tokenHelper.deleteTokens();
      unsetUser();
      navigate("/");

      return { success: true as const, message: data.message };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, deactivateAccount };
};

export const useRefreshToken = () => {
  const axios = axiosMain.create({
    baseURL: "https://rajapi-cop-auth-api.onrender.com",
  });

  const tokenHelper = new TokenHelper();
  const { refresh: token } = tokenHelper.getTokens();
  const { unsetUser } = useAuthStore();

  const refreshToken = async () => {
    try {
      const { data } = await axios.post<RefreshTokenResponse>(
        "/auth/token/refresh/",
        {
          refresh: token,
        },
      );

      if (data.access) {
        const tokenHelper = new TokenHelper();
        tokenHelper.setTokens(data.access);

        return data;
      } else {
        throw new Error();
      }
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(error);

      failedToast("Your session has expired, login to continue");

      unsetUser();
      const tokenHelper = new TokenHelper();
      tokenHelper.deleteTokens();
    }
  };

  return { refreshToken };
};

export const useVerifyToken = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const verifyToken = async (token: string) => {
    setLoading(true);
    try {
      const { data } = await axios.post<TokenVerifyResponse>(
        "/auth/token/verify",
        { token },
      );
      return { success: true as const, data };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, verifyToken };
};

export const useRequestPasswordReset = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const requestPasswordReset = async (email: string) => {
    setLoading(true);
    try {
      const { data } = await axios.post<DetailResponse>(
        "/auth/password-reset/",
        { email },
      );
      successToast(data.detail);
      return { success: true as const, detail: data.detail };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, requestPasswordReset };
};

export const useConfirmPasswordReset = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const confirmPasswordReset = async (
    uidb64: string,
    token: string,
    newPassword: string,
    confirmPassword: string,
  ) => {
    setLoading(true);
    try {
      const { data } = await axios.post<DetailResponse>(
        `/auth/reset-password/${uidb64}/${token}`,
        {
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      );
      successToast(data.detail);
      return { success: true as const, detail: data.detail };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, confirmPasswordReset };
};

export const useVerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const verifyEmail = async (uidb64: string, token: string) => {
    setLoading(true);
    try {
      const { data } = await axios.get<DetailResponse>(
        `/auth/verify-email/${uidb64}/${token}/`,
      );
      return { success: true as const, detail: data.detail };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, verifyEmail };
};

export const useResendVerificationEmail = () => {
  const [loading, setLoading] = useState(false);
  const { axios } = useAxios(ApiModules.AUTH);

  const resendVerificationEmail = async (email: string) => {
    setLoading(true);
    try {
      // ⚠️ GUESSED ENDPOINT — not confirmed against a real backend yet.
      // Follows the same shape as /auth/password-reset/ (POST { email } -> { detail }).
      // Update this path/method/payload once the backend implements it for real.
      const { data } = await axios.post<DetailResponse>(
        "/auth/resend-verification/",
        { email },
      );
      successToast(data.detail);
      return { success: true as const, detail: data.detail };
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.log(
        "Full error response:",
        error.response?.data || error.message,
      );
      return { success: false as const, error };
    } finally {
      setLoading(false);
    }
  };

  return { loading, resendVerificationEmail };
};
